import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Layout, Image, Bell, Grid, Code, Megaphone, Eye } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface HomepageConfig {
  bannerSection: {
    enabled: boolean;
    title: string;
    subtitle: string;
    backgroundImage: string;
    ctaText: string;
    ctaLink: string;
  };
  noticeSection: {
    enabled: boolean;
    position: 'top' | 'floating';
    maxCount: number;
  };
  featureCards: {
    enabled: boolean;
    cards: { title: string; desc: string; icon: string; link: string }[];
  };
  examCategories: {
    enabled: boolean;
    title: string;
    showCount: boolean;
  };
  codePlayground: {
    enabled: boolean;
    title: string;
    languages: string[];
  };
  ctaSection: {
    enabled: boolean;
    title: string;
    buttonText: string;
    buttonLink: string;
  };
}

const defaultConfig: HomepageConfig = {
  bannerSection: {
    enabled: true,
    title: '编程刷题平台',
    subtitle: '专注青少年编程教育，助力科技特长生成长',
    backgroundImage: '',
    ctaText: '开始刷题',
    ctaLink: '/exam-categories',
  },
  noticeSection: {
    enabled: true,
    position: 'floating',
    maxCount: 5,
  },
  featureCards: {
    enabled: true,
    cards: [
      { title: '真题题库', desc: '5000+真题', icon: 'Book', link: '/exam-categories' },
      { title: '在线编程', desc: 'Scratch/Python/C++', icon: 'Code', link: '/python' },
      { title: '错题本', desc: '智能复习', icon: 'Bookmark', link: '/wrong-questions' },
    ],
  },
  examCategories: {
    enabled: true,
    title: '考试分类',
    showCount: true,
  },
  codePlayground: {
    enabled: true,
    title: '在线编程',
    languages: ['scratch', 'python', 'cpp'],
  },
  ctaSection: {
    enabled: true,
    title: '立即开始你的编程之旅',
    buttonText: '免费试用',
    buttonLink: '/register',
  },
};

export default function HomepageEditor() {
  const [config, setConfig] = useState<HomepageConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<keyof HomepageConfig>('bannerSection');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await supabase
        .from('homepage_config')
        .select('*')
        .eq('id', 'default')
        .maybeSingle();
      if (data?.config) {
        setConfig({ ...defaultConfig, ...data.config });
      }
    } catch (e) {
      
    }
    setLoading(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await supabase.from('homepage_config').upsert({
        id: 'default',
        config,
        updated_at: new Date().toISOString(),
      });
      alert('保存成功');
    } catch (e) {
      alert('保存失败');
    }
    setSaving(false);
  };

  const updateSection = <K extends keyof HomepageConfig>(
    section: K,
    updates: Partial<HomepageConfig[K]>
  ) => {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...updates },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { key: 'bannerSection', label: 'Banner', icon: Image },
    { key: 'noticeSection', label: '公告', icon: Bell },
    { key: 'featureCards', label: '功能卡', icon: Grid },
    { key: 'examCategories', label: '考试分类', icon: Layout },
    { key: 'codePlayground', label: '编程区', icon: Code },
    { key: 'ctaSection', label: 'CTA', icon: Megaphone },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Layout className="w-6 h-6" />
          首页可视化编辑器
        </h1>
        <div className="flex gap-3">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="w-4 h-4" />
            {previewMode ? '编辑模式' : '预览'}
          </button>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? '保存中...' : '保存配置'}
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* 左侧导航 */}
        <div className="w-48 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as keyof HomepageConfig)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === tab.key
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* 右侧编辑区 */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 p-6">
          {previewMode ? (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-700">配置预览</h3>
              <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto">
                {JSON.stringify(config, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Banner设置 */}
              {activeTab === 'bannerSection' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      checked={config.bannerSection.enabled}
                      onChange={(e) =>
                        updateSection('bannerSection', { enabled: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    <span className="font-medium">启用Banner模块</span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        标题
                      </label>
                      <input
                        type="text"
                        value={config.bannerSection.title}
                        onChange={(e) =>
                          updateSection('bannerSection', { title: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        副标题
                      </label>
                      <input
                        type="text"
                        value={config.bannerSection.subtitle}
                        onChange={(e) =>
                          updateSection('bannerSection', { subtitle: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        按钮文字
                      </label>
                      <input
                        type="text"
                        value={config.bannerSection.ctaText}
                        onChange={(e) =>
                          updateSection('bannerSection', { ctaText: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* 公告设置 */}
              {activeTab === 'noticeSection' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      checked={config.noticeSection.enabled}
                      onChange={(e) =>
                        updateSection('noticeSection', { enabled: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    <span className="font-medium">启用公告模块</span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        显示位置
                      </label>
                      <select
                        value={config.noticeSection.position}
                        onChange={(e) =>
                          updateSection('noticeSection', {
                            position: e.target.value as 'top' | 'floating',
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="top">顶部</option>
                        <option value="floating">悬浮</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        最大显示数量
                      </label>
                      <input
                        type="number"
                        value={config.noticeSection.maxCount}
                        onChange={(e) =>
                          updateSection('noticeSection', {
                            maxCount: parseInt(e.target.value),
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* 其他模块简化显示 */}
              {activeTab === 'featureCards' && (
                <div className="text-gray-500">功能卡片配置（简化版）</div>
              )}
              {activeTab === 'examCategories' && (
                <div className="text-gray-500">考试分类配置（简化版）</div>
              )}
              {activeTab === 'codePlayground' && (
                <div className="text-gray-500">编程区配置（简化版）</div>
              )}
              {activeTab === 'ctaSection' && (
                <div className="text-gray-500">CTA配置（简化版）</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
